/**
 * @file Modal listing bidder activity and company-scoped notes.
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createBidderNote, deleteBidderNote, getBidderActivity, getBidderNotes } from '../api/bidders';
import { Loader, AlertTriangle, X, Trash2 } from 'lucide-react';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { BIDDER_NOTE_MAX_LENGTH, clampInput } from '../utils/inputLimits';

const EVENT_LABELS = {
  print_4x6: 'Printed 4x6 item list',
  print_thermal: 'Printed 3-1/8" receipt',
  picked: 'Marked loaded out',
  unpicked: 'Marked unloaded',
  marked_picked: 'Marked picked',
  unmarked_picked: 'Unmarked picked',
  scan_opened: 'Opened scan page',
  scan_completed: 'Completed scan',
};

function formatActivityDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function BidderHistoryModal({ bidderId, bidderName, onClose }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('history');
  const [draft, setDraft] = useState('');
  const [noteError, setNoteError] = useState('');

  const { data: activity = [], isLoading, error } = useQuery({
    queryKey: ['bidder-activity', bidderId],
    queryFn: () => getBidderActivity(bidderId),
    enabled: !!bidderId,
  });

  const notesQuery = useQuery({
    queryKey: ['bidder-notes', bidderId],
    queryFn: () => getBidderNotes(bidderId),
    enabled: !!bidderId && tab === 'notes',
  });

  const saveNote = useMutation({
    mutationFn: () => createBidderNote(bidderId, draft.trim()),
    onSuccess: () => {
      setDraft('');
      setNoteError('');
      queryClient.invalidateQueries({ queryKey: ['bidder-notes', bidderId] });
    },
    onError: (err) => setNoteError(err.message || 'Failed to save note'),
  });

  const removeNote = useMutation({
    mutationFn: (noteId) => deleteBidderNote(bidderId, noteId),
    onSuccess: () => {
      setNoteError('');
      queryClient.invalidateQueries({ queryKey: ['bidder-notes', bidderId] });
    },
    onError: (err) => setNoteError(err.message || 'Failed to delete note'),
  });

  const displayName = decodeHtmlEntities(bidderName) || bidderId;
  const notes = notesQuery.data || [];

  const submitDraft = () => {
    if (!draft.trim() || saveNote.isPending) return;
    saveNote.mutate();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: '720px', width: '95%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3>{displayName}</h3>
            <p style={{ margin: '6px 0 0', fontSize: '14px', color: 'var(--color-gray-600)' }}>
              <span style={{ color: 'var(--color-gray-500)' }}>#{bidderId}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            style={{ padding: '6px 8px', minWidth: 'auto' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'history'}
            className={`modal-tab${tab === 'history' ? ' active' : ''}`}
            onClick={() => setTab('history')}
          >
            Bidder History
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'notes'}
            className={`modal-tab${tab === 'notes' ? ' active' : ''}`}
            onClick={() => setTab('notes')}
          >
            Bidder Notes
          </button>
        </div>

        {tab === 'history' && (
          <>
            {isLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '20px 0' }}>
                <Loader size={20} className="spin" />
                Loading history…
              </div>
            )}

            {error && (
              <div className="error" style={{ marginBottom: '12px' }}>
                <AlertTriangle size={16} aria-hidden />
                {error.message || 'Failed to load bidder history'}
              </div>
            )}

            {!isLoading && !error && activity.length === 0 && (
              <p style={{ color: 'var(--color-gray-600)', margin: '16px 0' }}>No activity recorded yet.</p>
            )}

            {!isLoading && !error && activity.length > 0 && (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ paddingRight: '28px' }}>Date / Time</th>
                      <th style={{ paddingRight: '24px' }}>Auction Group</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((row) => (
                      <tr key={row.id}>
                        <td style={{ whiteSpace: 'nowrap', paddingRight: '28px' }}>
                          {formatActivityDate(row.created_at)}
                        </td>
                        <td style={{ paddingRight: '24px' }}>
                          {decodeHtmlEntities(row.auction_group_title) || `Group #${row.auction_group_id}`}
                        </td>
                        <td>{EVENT_LABELS[row.event_type] || row.event_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === 'notes' && (
          <div>
            {notesQuery.isLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '20px 0' }}>
                <Loader size={20} className="spin" />
                Loading notes…
              </div>
            )}
            {notesQuery.error && (
              <div className="error" style={{ marginBottom: '12px' }}>
                <AlertTriangle size={16} aria-hidden />
                {notesQuery.error.message || 'Failed to load notes'}
              </div>
            )}
            {!notesQuery.isLoading && !notesQuery.error && notes.length === 0 && (
              <p style={{ color: 'var(--color-gray-600)', margin: '16px 0' }}>No notes yet.</p>
            )}
            {!notesQuery.isLoading && notes.length > 0 && (
              <div className="bidder-notes-list">
                {notes.map((note) => (
                  <article key={note.id} className="bidder-note-card">
                    <div className="bidder-note-meta">
                      <strong>{note.authorName || 'Unknown'}</strong>
                      <span>{formatActivityDate(note.createdAt)}</span>
                    </div>
                    <p className="bidder-note-body">{note.body}</p>
                    <button
                      type="button"
                      className="bidder-note-delete"
                      aria-label="Delete note"
                      disabled={removeNote.isPending}
                      onClick={() => {
                        if (window.confirm('Delete this note?')) {
                          removeNote.mutate(note.id);
                        }
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </article>
                ))}
              </div>
            )}

            {noteError && (
              <div className="error" style={{ marginBottom: '12px' }}>
                {noteError}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitDraft();
              }}
            >
              <textarea
                value={draft}
                maxLength={BIDDER_NOTE_MAX_LENGTH}
                onChange={(e) => setDraft(clampInput(e.target.value, BIDDER_NOTE_MAX_LENGTH))}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitDraft();
                  }
                }}
                rows={4}
                placeholder="Add a note about this bidder"
                aria-describedby="bidder-note-hint"
                style={{ width: '100%', marginBottom: 8 }}
              />
              <p id="bidder-note-hint" className="bidder-note-hint">
                Enter to save · Shift+Enter for a new line
              </p>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saveNote.isPending || !draft.trim()}
              >
                {saveNote.isPending ? 'Saving…' : 'Save notes'}
              </button>
            </form>
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BidderHistoryModal;
